<template>
  <v-dialog v-model="showDialog" max-width="760" persistent>
    <v-card>
      <v-card-title class="d-flex align-center">
        <span>Review External Server</span>
        <v-spacer />
        <v-btn icon variant="text" @click="close"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>
      <v-card-text>
        <v-alert v-if="submitError" type="error" density="compact" class="mb-3">
          {{ submitError }}
        </v-alert>

        <div class="text-subtitle-2 mb-2">Server Key</div>
        <div class="mb-3">{{ draft?.name }}</div>

        <div class="text-subtitle-2 mb-2">Display Name</div>
        <div class="mb-3">{{ draft?.displayName }}</div>

        <div class="text-subtitle-2 mb-2">URL</div>
        <div class="mb-3 break-url">{{ draft?.url }}</div>

        <div class="text-subtitle-2 mb-2">Auth Mode</div>
        <div class="mb-3">{{ draft?.authMode }}</div>

        <template v-if="draft?.authMode === 'oauth2' && draft?.oauthTemplate">
          <div class="text-subtitle-2 mb-2">Authorization Provider</div>
          <div class="mb-2 break-url">{{ draft.oauthTemplate.authorizationServerIssuer || draft.oauthTemplate.authorizationEndpoint }}</div>
          <div class="mb-2 break-url"><strong>Authorization:</strong> {{ draft.oauthTemplate.authorizationEndpoint }}</div>
          <div class="mb-3 break-url"><strong>Token:</strong> {{ draft.oauthTemplate.tokenEndpoint }}</div>

          <div class="text-subtitle-2 mb-2">Registration Mode</div>
          <div class="mb-3">
            {{ registrationModeLabel }}
          </div>

          <div v-if="resolvedScopes.length > 0" class="mb-3">
            <div class="text-subtitle-2 mb-2">Scopes</div>
            <div>{{ resolvedScopes.join(', ') }}</div>
          </div>

          <div v-if="showManualCredentialInputs" class="mb-3">
            <v-text-field v-model="installClientId" label="Client ID" variant="outlined" class="mb-3" />
            <v-text-field v-model="installClientSecret" label="Client Secret" variant="outlined" class="mb-3" />
            <v-text-field
              v-model="installScopesValue"
              label="Scopes Override"
              hint="Comma-separated optional scopes."
              persistent-hint
              variant="outlined"
            />
          </div>

          <v-alert
            v-else-if="draft.oauthTemplate.registrationMode === 'cimd' || draft.oauthTemplate.registrationMode === 'dcr'"
            type="info"
            density="compact"
            variant="tonal"
            class="mb-3"
          >
            {{
              draft.oauthTemplate.registrationMode === 'cimd'
                ? 'This shared definition uses automatic client identity. Manual client credentials are intentionally unavailable for later installs.'
                : 'This shared definition uses automatic client registration. Manual client credentials are intentionally unavailable for later installs.'
            }}
          </v-alert>
        </template>

        <div class="mb-3">
          <div class="text-subtitle-2 mb-2">Secret Field Names</div>
          <div>{{ secretFieldNames }}</div>
        </div>

        <div v-if="secretFields.length > 0" class="mb-3">
          <div class="text-subtitle-2 mb-2">User Secrets</div>
          <div
            v-for="field in secretFields"
            :key="field.name"
            class="mb-3"
          >
            <div class="text-caption text-medium-emphasis mb-1">
              {{ field.label }}
              <span v-if="field.required" class="text-error">*</span>
            </div>
            <div v-if="field.description" class="text-body-2 mb-2">{{ field.description }}</div>
            <v-text-field
              v-model="installSecretValues[field.name]"
              :label="field.label"
              type="password"
              variant="outlined"
              density="compact"
            />
          </div>
        </div>

        <v-alert type="info" variant="tonal" density="compact">
          This server definition will be visible to other MissionSquad users. Your secrets and OAuth credentials will remain private to your account.
        </v-alert>
      </v-card-text>
      <v-card-actions class="px-4 pb-4">
        <v-spacer />
        <v-btn variant="text" @click="close">Cancel</v-btn>
        <v-btn color="primary" :loading="submitting" @click="submit">Confirm</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import { isApiError } from '@/types/api'
import {
  CreateExternalMcpServerRequest,
  InstallExternalMcpServerRequest,
  UserVisibleMcpServer
} from '@/types/mcp'
import { Options, Vue } from 'vue-class-component'

function parseScopes(value: string): string[] | undefined {
  const scopes = value
    .split(',')
    .map((scope) => scope.trim())
    .filter(Boolean)
  return scopes.length > 0 ? scopes : undefined
}

@Options({
  props: {
    show: Boolean,
    draft: Object,
    existingServer: Object,
    initialSecrets: Array
  },
  emits: ['close', 'installed'],
  watch: {
    show: {
      handler(value: boolean) {
        if (value === true) {
          ;(this as any).primeInstallForm()
        }
      },
      immediate: true
    }
  }
})
export default class ExternalMcpServerInstallDialog extends Vue {
  show!: boolean
  draft!: CreateExternalMcpServerRequest | null
  existingServer!: UserVisibleMcpServer | null
  initialSecrets!: Array<{ name: string; value: string }>
  submitting = false
  submitError = ''
  installClientId = ''
  installClientSecret = ''
  installScopesValue = ''
  installSecretValues: Record<string, string> = {}

  get showDialog(): boolean {
    return this.show
  }

  get resolvedScopes(): string[] {
    return (
      parseScopes(this.installScopesValue) ||
      this.draft?.oauthClientConfig?.scopes ||
      this.draft?.oauthTemplate?.challengedScopes ||
      this.draft?.oauthTemplate?.scopesSupported ||
      []
    )
  }

  get registrationModeLabel(): string {
    if (this.draft?.oauthTemplate?.manualClientCredentialsAllowed) {
      return 'Automatic registration with optional manual client ID / secret'
    }
    if (this.draft?.oauthTemplate?.registrationMode === 'cimd') {
      return 'Automatic client identity (CIMD)'
    }
    if (this.draft?.oauthTemplate?.registrationMode === 'dcr') {
      return 'Automatic client registration'
    }
    return 'Manual client ID / secret'
  }

  get showManualCredentialInputs(): boolean {
    return (
      this.draft?.authMode === 'oauth2' &&
      (
        this.draft?.oauthTemplate?.registrationMode === 'manual' ||
        this.draft?.oauthTemplate?.manualClientCredentialsAllowed === true
      )
    )
  }

  get manualClientCredentialsRequired(): boolean {
    return this.draft?.authMode === 'oauth2' && this.draft?.oauthTemplate?.registrationMode === 'manual'
  }

  get secretFieldNames(): string {
    const names = this.draft?.secretFields?.map((field) => field.name) || []
    return names.length > 0 ? names.join(', ') : 'None'
  }

  get secretFields() {
    return this.draft?.secretFields || []
  }

  primeInstallForm(): void {
    this.submitError = ''
    this.installClientId = this.draft?.oauthClientConfig?.clientId || ''
    this.installClientSecret = ''
    this.installScopesValue = (this.draft?.oauthClientConfig?.scopes || []).join(', ')
    this.installSecretValues = (this.draft?.secretFields || []).reduce((acc, field) => {
      acc[field.name] = this.initialSecrets?.find((secret) => secret.name === field.name)?.value || ''
      return acc
    }, {} as Record<string, string>)
  }

  buildSecretsPayload(): Array<{ name: string; value: string }> {
    return this.secretFields
      .map((field) => ({
        name: field.name,
        value: (this.installSecretValues[field.name] || '').trim()
      }))
      .filter((secret) => secret.value.length > 0)
  }

  close(): void {
    this.submitError = ''
    this.$emit('close')
  }

  buildCreatePayload(): CreateExternalMcpServerRequest {
    return { ...(this.draft as CreateExternalMcpServerRequest) }
  }

  buildInstallPayload(): InstallExternalMcpServerRequest {
    const scopes = parseScopes(this.installScopesValue)
    const hasManualCredentialOverride =
      this.showManualCredentialInputs &&
      (this.installClientId.trim() !== '' || this.installClientSecret.trim() !== '' || !!scopes)

    return {
      enabled: true,
      ...(hasManualCredentialOverride
        ? {
            oauthClientConfig: {
              ...(this.installClientId.trim() ? { clientId: this.installClientId.trim() } : {}),
              ...(this.installClientSecret.trim() ? { clientSecret: this.installClientSecret.trim() } : {}),
              scopes
            }
          }
        : scopes
          ? {
              oauthClientConfig: {
                scopes
              }
            }
          : {})
    }
  }

  async submit(): Promise<void> {
    if (!this.draft) return

    if (this.manualClientCredentialsRequired && !this.installClientId.trim()) {
      this.submitError = 'Client ID is required for manual OAuth installs.'
      return
    }

    const missingRequiredSecrets = this.secretFields
      .filter((field) => field.required && !(this.installSecretValues[field.name] || '').trim())
      .map((field) => field.label || field.name)

    if (missingRequiredSecrets.length > 0) {
      this.submitError = `Missing required secrets: ${missingRequiredSecrets.join(', ')}`
      return
    }

    this.submitting = true
    this.submitError = ''

    try {
      let server: UserVisibleMcpServer
      if (this.existingServer && !this.existingServer.installed) {
        server = await this.$store.dispatch('installExternalServer', {
          serverName: this.existingServer.name,
          body: this.buildInstallPayload()
        })
      } else {
        try {
          server = await this.$store.dispatch('createExternalServer', this.buildCreatePayload())
        } catch (error) {
          if (isApiError(error) && error.statusCode === 409) {
            server = await this.$store.dispatch('installExternalServer', {
              serverName: this.draft.name,
              body: this.buildInstallPayload()
            })
          } else {
            throw error
          }
        }
      }

      const secrets = this.buildSecretsPayload()
      if (secrets.length > 0) {
        await this.$store.dispatch('saveExternalServerSecrets', {
          serverName: server.name,
          secrets
        })
      }

      this.$emit('installed', {
        server,
        needsOAuth: server.authMode === 'oauth2' && server.authState !== 'connected'
      })
    } catch (error) {
      this.submitError = isApiError(error) ? error.message : error instanceof Error ? error.message : 'Install failed'
    } finally {
      this.submitting = false
    }
  }
}
</script>

<style scoped>
.break-url {
  overflow-wrap: anywhere;
}
</style>
